// import components
import {
  Box,
  FormControlLabel,
  IconButton,
  Switch,
  Typography
} from '@mui/material';
import TextField from '@mui/material/TextField';
import RecurrenceRule from '../RecurrenceRule/RecurrenceRule';

// import hooks
import { useTranslation } from 'react-i18next';

// import styles
import {
  StyledBoxFlex,
  StyledBoxFlexAlignCenter,
  StyledBoxFlexColumnLargeGap,
  StyledBoxFlexRow
} from '../Box/Box.styles';

// import types
import type { FC } from 'react';
import type { PlanningCycleDialogContentProps } from './PlanningCycle.types';

// import utils
import { formatByLocale } from 'utils/date';
import { Icon } from '@sdb/planning-ui';

const PlanningCycleDialogContent: FC<PlanningCycleDialogContentProps> = ({
  type,
  setRecurrenceRule = () => {},
  recurrenceRule,
  cycleWindow,
  setCycleWindow,
  rounds,
  updateRound,
  deleteRound,
  addRound,
  rosterCycles
}) => {
  'use memo';

  const { t } = useTranslation('common');

  return (
    <StyledBoxFlexRow>
      <StyledBoxFlexColumnLargeGap>
        <RecurrenceRule
          onRecurrenceRuleChange={setRecurrenceRule}
          recurrenceRule={recurrenceRule}
        />

        {recurrenceRule && (
          <Box>
            <Typography variant="h6">{t('planningsWindow')}</Typography>
            <StyledBoxFlexAlignCenter>
              <Typography variant="body1">
                {t(type === 'PUBLISHED' ? 'publishes' : 'visible')}
              </Typography>
              <TextField
                value={cycleWindow}
                type="number"
                variant="outlined"
                size="small"
                className="inline-input"
                onChange={(e) => setCycleWindow(parseInt(e.target.value, 10))}
                inputProps={{ min: 0 }}
              />
              {recurrenceRule?.periodType && (
                <Typography variant="body1">
                  {t(
                    `${recurrenceRule.periodType.toLowerCase()}${
                      cycleWindow === 1 ? '' : 's'
                    }`
                  ).toLowerCase()}{' '}
                  {t('inAdvance')}
                </Typography>
              )}
            </StyledBoxFlexAlignCenter>
          </Box>
        )}

        {recurrenceRule && (
          <Box>
            <Typography variant="h6">{t('rounds')}</Typography>
            {rounds.map((round, roundIndex) => (
              <Box key={roundIndex}>
                <StyledBoxFlex>{`${t('round')} ${
                  roundIndex + 1
                }:`}</StyledBoxFlex>
                <StyledBoxFlexAlignCenter>
                  <Typography variant="body1">{`${t('from')} ${t(
                    'day'
                  ).toLowerCase()}`}</Typography>
                  <TextField
                    value={round.startIndex}
                    type="number"
                    variant="outlined"
                    size="small"
                    className="inline-input"
                    onChange={(e) =>
                      updateRound(roundIndex, {
                        ...round,
                        startIndex: parseInt(e.target.value, 10)
                      })
                    }
                    inputProps={{ min: 1 }}
                  />
                  <Typography variant="body1">{`${t('until')} ${t(
                    'day'
                  ).toLowerCase()}`}</Typography>
                  <TextField
                    value={round.endIndex}
                    type="number"
                    variant="outlined"
                    size="small"
                    className="inline-input"
                    onChange={(e) =>
                      updateRound(roundIndex, {
                        ...round,
                        endIndex: parseInt(e.target.value, 10)
                      })
                    }
                    inputProps={{ min: 1 }}
                  />
                  <FormControlLabel
                    label={t(`autoConnect`)}
                    control={
                      <Switch
                        checked={round.autoConnect}
                        onChange={(e) =>
                          updateRound(roundIndex, {
                            ...round,
                            autoConnect: e.target.checked
                          })
                        }
                      />
                    }
                  />
                  <IconButton
                    onClick={() => deleteRound(roundIndex)}
                    color="error"
                    size="small"
                  >
                    <Icon type="trash-can" fontSize="small" />
                  </IconButton>
                </StyledBoxFlexAlignCenter>
              </Box>
            ))}
            <IconButton onClick={() => addRound()} color="primary" size="small">
              <Icon type="plus" fontSize="small" />
            </IconButton>
          </Box>
        )}
      </StyledBoxFlexColumnLargeGap>

      {rosterCycles && (
        <Box>
          <Typography variant="h6">{t('examples')}</Typography>
          {rosterCycles.map(({ cycleDate, nextOccurrence, rounds }) => (
            <Box key={nextOccurrence}>
              <StyledBoxFlexAlignCenter sx={{ width: 'max-content' }}>
                {t(type === 'PUBLISHED' ? 'publishes' : 'visible')}
                &nbsp; {t('until')} &nbsp;
                <b>{formatByLocale(new Date(cycleDate), 'P')}</b>
                &nbsp; {t('on')} &nbsp;
                <i>{formatByLocale(new Date(nextOccurrence), 'P')}</i>
              </StyledBoxFlexAlignCenter>
              {rounds &&
                rounds.map(({ start, end, autoConnect }, index) => (
                  <StyledBoxFlexAlignCenter
                    key={index}
                    sx={{ width: 'max-content' }}
                  >
                    &nbsp; <b>{`${t('round')} ${index + 1}:`}</b> &nbsp;
                    {t('from')} &nbsp;{' '}
                    <i>{formatByLocale(new Date(start), 'P')}</i>
                    &nbsp; {t('until')} &nbsp;{' '}
                    <i>{formatByLocale(new Date(end), 'P')}</i>
                    &nbsp; {autoConnect && `${t('withAutoConnect')}`}
                  </StyledBoxFlexAlignCenter>
                ))}
            </Box>
          ))}
        </Box>
      )}
    </StyledBoxFlexRow>
  );
};

export default PlanningCycleDialogContent;
